import React, { useMemo } from 'react';
import { useDispatch, batch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';

import LowerFeedHeader from './LowerFeedHeader';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import optimizeImage from '../../../../shared/utils/imageOptimizer';

import {
  setActiveReduxModal,
  setIsFeedShowing,
  setIsTvMode,
} from '../../ducks/feedActions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const FeedToggleButton = styled('button')`
  align-self: center;
  display: flex;
  align-items: center;
  height: 25px;
  margin-bottom: 0;
  border: 0px;
  font-size: 25px;
  font-weight: 500;
  cursor: pointer;
`;

const FilterIconContainer = styled('div')`
  margin: ${(props) => (props.selectedGroups.length > 0 ? '5px 5px 0 5px' : '5px 16px 0 5px')};
  cursor: pointer;
  display: flex;
  flex-direction: row;
`;

const Logo = styled('a')`
  margin-right: 25px;
  background-image: ${(props) => `url(${props.logo})`};
  background-position: left 50%;
  background-size: contain;
  background-repeat: no-repeat;
  min-width: 35px;
  width: 65px;
  height: 35px;
  cursor: pointer;
  @media screen and (max-width: 540px) {
    margin-right: 0;
  }
`;

const MainHeader = styled('header')`
  background-color: ${(props) => props.theme.colorPrimary};
  display: flex;
  position: fixed;
  z-index: 1000;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  height: 49px;
  padding: 0 15px;
  box-sizing: border-box;
  justify-content: center;
  svg {
    vertical-align: unset !important;
  }
`;

const RightIcons = styled('div')`
  display: flex;
  width: 120px;
  justify-content: end;
  align-items: center;
  margin-right: 16px;
  @media screen and (max-width: 540px) {
    width: 80px;
    margin-right: 0;
  }
`;

const SelectedGroupsCount = styled('div')`
  height: 13px;
  width: 13px;
  border-radius: 50%;
  background-color: ${(props) => (props.isPrimaryDarkTheme ? '#ffffff' : '#444444')};
  color: ${(props) => (props.isPrimaryDarkTheme ? '#444444' : '#ffffff')};
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  top: -2px;
  right: 4px;
`;

const SpinnerWrapper = styled('div')`
  display: flex;
  height: 32px;
  width: 32px;
  margin-right: 10px;
  justify-content: center;
`;

const TvModeHeader = ({ isPrimaryDarkTheme, logo, scrollToTopOfFeed }) => {
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);
  const isPrCountLoading = useSelector(
    (state) => state.feed.ui.isPrCountLoading,
  );
  const isTopPrLoading = useSelector((state) => state.feed.ui.isTopPrLoading);
  const isFeedLoading = useSelector((state) => state.feed.ui.isFeedLoading);

  const dispatch = useDispatch();

  const isLoading = isTopPrLoading || isFeedLoading || isPrCountLoading;

  const handleClickFilterGroups = () => {
    dispatch(setActiveReduxModal('filter-groups-modal'));
    feedTracker('TV Mode', 'Group filter modal opened');
  };

  const handleSwitchToFeed = () => {
    batch(() => {
      dispatch(setIsTvMode(false));
      dispatch(setIsFeedShowing(true));
    });
    feedTracker('TV Mode', 'Exit TV Mode clicked');
  };

  const optimizedLogo = useMemo(() => optimizeImage(logo, {
    resize: {
      minWidth: 40,
      maxWidth: 80,
      minHeight: 40,
      maxHeight: 80,
      fit: 'contain',
    },
  }), [logo]);

  const renderSpinnerSection = () => (
    isLoading && (
      <SpinnerWrapper>
        <Spinner saving darkTheme={!isPrimaryDarkTheme} />
      </SpinnerWrapper>
    )
  );

  const renderFilterIconSection = () => (
    <FilterIconContainer
      aria-label='Select Group'
      onClick={handleClickFilterGroups}
      selectedGroups={selectedGroups}
    >
      <IcomoonReact
        iconSet={iconSet}
        size={32}
        icon='group'
        color={isPrimaryDarkTheme ? '#FFFFFF' : '#444444'}
      />
      {selectedGroups.length > 0 && (
        <SelectedGroupsCount isPrimaryDarkTheme={isPrimaryDarkTheme}>
          {selectedGroups.length}
        </SelectedGroupsCount>
      )}
    </FilterIconContainer>
  );

  const renderRightIconsSection = () => (
    <RightIcons className='align-self-end'>
      {renderSpinnerSection()}
      {renderFilterIconSection()}
      <FeedToggleButton onClick={handleSwitchToFeed}>
        <IcomoonReact
          iconSet={iconSet}
          size={12}
          icon='remove'
          color={isPrimaryDarkTheme ? '#FFFFFF' : '#444444'}
        />
      </FeedToggleButton>
    </RightIcons>
  );

  return (
    <MainHeader>
      <Logo
        href='/'
        id='logo'
        title='logo'
        className='align-self-start'
        logo={optimizedLogo}
      />
      <LowerFeedHeader
        isPrimaryDarkTheme
        scrollToTopOfFeed={scrollToTopOfFeed}
      />
      {renderRightIconsSection()}
    </MainHeader>
  );
};

TvModeHeader.propTypes = {
  isPrimaryDarkTheme: PropTypes.bool.isRequired,
  logo: PropTypes.string,
  scrollToTopOfFeed: PropTypes.func.isRequired,
};

TvModeHeader.defaultProps = {
  logo: '',
};

export default TvModeHeader;
